import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Badge } from "../ui/badge";
import { UserPlus, Users, X, Building2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fetchAvailableUsers, fetchUsers } from "../../services/crmServices/userService";
import { fetchAvailableDesks } from "../../services/crmServices/deskService";
import { bulkAssignLeads, bulkUpdateLeads } from "../../services/crmServices/leadService";
import { useAuth } from "../../hooks/useAuth";

const STORAGE_KEYS = {
    AGENTS: 'bulkAssignModal_agents',
    DESKS: 'bulkAssignModal_desks'
};

export function BulkAssignModal({ open, onClose, selectedLeads, onSuccess }) {
    const { user } = useAuth();
    
    const AGENTS_KEY = 'bulkAssignModal_agents';
    const DESKS_KEY = 'bulkAssignModal_desks';
    
    const loadFromLocalStorage = (key, defaultValue = []) => {
        try {
            const cached = localStorage.getItem(key);
            if (cached) {
                return JSON.parse(cached);
            }
        } catch (error) {
            console.error(`Error loading ${key} from localStorage:`, error);
        }
        return defaultValue;
    };
    
    const cachedAgents = loadFromLocalStorage(AGENTS_KEY, []);
    const cachedDesks = loadFromLocalStorage(DESKS_KEY, []);
    const cachedFloorManagers = cachedAgents.filter((user) => user.position?.role === "floorManager");
    
    const [agents, setAgents] = useState(cachedAgents);
    const [allAgents, setAllAgents] = useState(cachedAgents);
    const [floorManagers, setFloorManagers] = useState(cachedFloorManagers);
    const [desks, setDesks] = useState(cachedDesks);
    
    const [selectedAgent, setSelectedAgent] = useState("");
    const [selectedDesk, setSelectedDesk] = useState("");
    const [selectedFloorManager, setSelectedFloorManager] = useState("");
    const [selectedAgentDesk, setSelectedAgentDesk] = useState("");
    const [assignmentType, setAssignmentType] = useState("agent");
    const [loading, setLoading] = useState(false);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [floorManagersLoading, setFloorManagersLoading] = useState(false);
    const [desksLoading, setDesksLoading] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });

    const role = user?.position?.role;
    const canAssignToDesk = role === "admin" || role === "floorManager";
    const canAssignToFloorManager = role === "admin";

    useEffect(() => {
        if (open) {
            const cachedAgentsData = loadFromLocalStorage(AGENTS_KEY, []);
            const cachedDesksData = loadFromLocalStorage(DESKS_KEY, []);
            
            if (cachedAgentsData.length > 0) {
                setAllAgents(cachedAgentsData);
                setAgents(cachedAgentsData);
                const floorManagerUsers = cachedAgentsData.filter((user) => user.position?.role === "floorManager");
                setFloorManagers(floorManagerUsers);
            }
            
            if (cachedDesksData.length > 0) {
                setDesks(cachedDesksData);
            }
            
            if (cachedAgentsData.length === 0) {
                setAgentsLoading(true);
            }
            fetchAgents();
            
            if (cachedDesksData.length === 0) {
                setDesksLoading(true);
            }
            fetchDesksData();
            
            if (canAssignToFloorManager) {
                if (cachedAgentsData.length > 0) {
                    const floorManagerUsers = cachedAgentsData.filter((user) => user.position?.role === "floorManager");
                    setFloorManagers(floorManagerUsers);
                } else {
                    fetchFloorManagers();
                }
            }
        }
    }, [open, canAssignToFloorManager]);

    const fetchAgents = async () => {
        const hasCached = loadFromLocalStorage(AGENTS_KEY, []).length > 0;
        
        if (!hasCached) {
            setAgentsLoading(true);
        }
        
        try {
            const response = await fetchAvailableUsers();
            
            let allUsers = null;
            if (Array.isArray(response)) {
                allUsers = response;
            } else if (response?.data) {
                allUsers = Array.isArray(response.data) ? response.data : response.data;
            } else if (response?.users) {
                allUsers = response.users;
            }
            
            if (allUsers && Array.isArray(allUsers)) {
                setAllAgents(allUsers);
                setAgents(allUsers);
                
                const floorManagerUsers = allUsers.filter((user) => user.position?.role === "floorManager");
                setFloorManagers(floorManagerUsers);
                
                try {
                    const cleanUsers = allUsers.map(user => {
                        return {
                            _id: user._id,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            email: user.email,
                            position: user.position ? {
                                _id: user.position._id,
                                name: user.position.name,
                                role: user.position.role
                            } : null,
                            agentDesk: user.agentDesk ? {
                                _id: user.agentDesk._id,
                                name: user.agentDesk.name
                            } : null
                        };
                    });
                    
                    const jsonString = JSON.stringify(cleanUsers);
                    localStorage.setItem(AGENTS_KEY, jsonString);
                } catch (storageError) {
                    console.error("localStorage error for agents:", storageError);
                }
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            if (!hasCached) {
                toast.error("Failed to load users");
            }
        } finally {
            if (!hasCached) {
                setAgentsLoading(false);
            }
        }
    };

    const fetchDesksData = async () => {
        const hasCached = loadFromLocalStorage(DESKS_KEY, []).length > 0;
        
        if (!hasCached) {
            setDesksLoading(true);
        }
        
        try {
            const response = await fetchAvailableDesks();
            
            let desksData = null;
            if (Array.isArray(response)) {
                desksData = response;
            } else if (response?.data) {
                desksData = Array.isArray(response.data) ? response.data : response.data;
            }
            
            if (desksData && Array.isArray(desksData) && desksData.length > 0) {
                setDesks(desksData);
                
                try {
                    const jsonString = JSON.stringify(desksData);
                    localStorage.setItem(DESKS_KEY, jsonString);
                } catch (storageError) {
                    console.error("localStorage error for desks:", storageError);
                }
            }
        } catch (error) {
            console.error("Error fetching desks:", error);
            if (!hasCached) {
                toast.error("Failed to load desks");
            }
        } finally {
            if (!hasCached) {
                setDesksLoading(false);
            }
        }
    };

    const fetchFloorManagers = async () => {
        setFloorManagersLoading(true);
        try {
            const response = await fetchAvailableUsers();
            if (response?.data) {
                const floorManagerUsers = response.data.filter((user) => user.position?.role === "floorManager");
                setFloorManagers(floorManagerUsers);
            }
        } catch (error) {
            console.error("Error fetching floor managers:", error);
            toast.error("Failed to load floor managers");
        } finally {
            setFloorManagersLoading(false);
        }
    };

    const handleAgentDeskChange = (deskId) => {
        setSelectedAgentDesk(deskId);
        setSelectedAgent("");

        if (deskId) {
            const filteredUsers = allAgents.filter((agent) => agent.agentDesk?._id === deskId);
            setAgents(filteredUsers);
        } else {
            setAgents(allAgents);
        }
    };

    const handleAssign = async () => {
        if (assignmentType === "agent" && !selectedAgent) {
            toast.error("Please select a user");
            return;
        }

        if (assignmentType === "desk" && !selectedDesk) {
            toast.error("Please select a desk");
            return;
        }

        if (assignmentType === "floorManager" && !selectedFloorManager) {
            toast.error("Please select a floor manager");
            return;
        }

        setLoading(true);
        const startTime = Date.now();
        const totalLeads = selectedLeads.length;
        setProgress({ current: 0, total: totalLeads, percentage: 0 });

        const targetText = assignmentType === "agent" 
            ? "user" 
            : assignmentType === "floorManager"
            ? "floor manager"
            : "desk";

        const progressToast = toast.loading(`Preparing to assign ${totalLeads.toLocaleString()} leads to ${targetText}...`);

        try {
            const leadIds = selectedLeads.map((lead) => lead._id);

            let assignmentData;
            if (assignmentType === "agent") {
                assignmentData = {
                    leadIds,
                    updates: {
                        agent: selectedAgent,
                        desk: allAgents.find((agent) => agent._id === selectedAgent)?.agentDesk?._id || null
                    }
                };
            } else if (assignmentType === "floorManager") {
                assignmentData = {
                    leadIds,
                    updates: {
                        floorManager: selectedFloorManager,
                        desk: null,
                        agent: null
                    }
                };
            } else {
                assignmentData = {
                    leadIds,
                    updates: {
                        desk: selectedDesk,
                        agent: null
                    }
                };
            }

            let progressStep = 0;
            const progressInterval = setInterval(() => {
                setProgress(prev => {
                    progressStep++;
                    const progressValue = Math.min(95, Math.round(100 * (1 - Math.exp(-progressStep / 8))));
                    const currentCount = Math.round((progressValue / 100) * totalLeads);
                    const elapsed = Math.round((Date.now() - startTime) / 1000);
                    const estimated = progressValue > 5 ? Math.round((elapsed / progressValue) * 100) : 0;
                    const remaining = Math.max(0, estimated - elapsed);
                    
                    toast.loading(
                        `Assigning ${currentCount.toLocaleString()} / ${totalLeads.toLocaleString()} leads (${progressValue}%) • ${remaining}s remaining`,
                        { id: progressToast }
                    );
                    return { current: currentCount, total: totalLeads, percentage: progressValue };
                });
            }, 300);

            const response = await bulkUpdateLeads(assignmentData);

            clearInterval(progressInterval);
            const elapsed = Math.round((Date.now() - startTime) / 1000);
            toast.dismiss(progressToast);
            setProgress({ current: totalLeads, total: totalLeads, percentage: 100 });

            if (response?.status === 200) {
                const actualCount = response?.data?.count || selectedLeads.length;
                const timeText = elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
                const successMessage = `✓ Successfully assigned ${actualCount.toLocaleString()} leads to ${targetText} in ${timeText}`;
                toast.success(successMessage, { duration: 5000 });
                onSuccess();
                handleClose();
            } else {
                const errorMessage =
                    assignmentType === "agent"
                        ? "Failed to assign leads to user"
                        : assignmentType === "floorManager"
                        ? "Failed to assign leads to floor manager"
                        : "Failed to assign leads to desk";
                toast.error(errorMessage);
            }
        } catch (error) {
            toast.dismiss(progressToast);
            console.error("Error assigning leads:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to assign leads";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
            setProgress({ current: 0, total: 0, percentage: 0 });
        }
    };

    const handleClose = () => {
        setSelectedAgent("");
        setSelectedDesk("");
        setSelectedFloorManager("");
        setSelectedAgentDesk("");
        setAssignmentType("agent");
        setAgents(allAgents);
        onClose();
    };

    const assignmentOptions = [
        { value: "agent", label: "User", description: "Assign leads to a specific user", icon: UserPlus, color: "blue" },
        ...(canAssignToDesk ? [{ value: "desk", label: "Desk", description: "Assign leads to a desk", icon: Building2, color: "violet" }] : []),
        ...(canAssignToFloorManager ? [{ value: "floorManager", label: "Floor Manager", description: "Assign to a floor manager", icon: Users, color: "emerald" }] : [])
    ];

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-xl max-h-[85vh] flex flex-col p-0 gap-0 rounded-xl overflow-hidden">
                {/* Header */}
                <div className="px-6 py-5 bg-gradient-to-r from-slate-50 to-white border-b">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-100 text-indigo-600">
                            <UserPlus className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                            <DialogHeader className="p-0 space-y-0.5">
                                <DialogTitle className="text-lg font-semibold text-gray-900">
                                    Assign Leads
                                </DialogTitle>
                                <DialogDescription className="text-sm text-gray-500">
                                    Assign {selectedLeads.length} lead{selectedLeads.length !== 1 ? "s" : ""} to {
                                        assignmentType === "agent" ? "a user"
                                        : assignmentType === "floorManager" ? "a floor manager"
                                        : "a desk"
                                    }
                                </DialogDescription>
                            </DialogHeader>
                        </div>
                        <Badge variant="secondary" className="text-xs font-medium">
                            {selectedLeads.length} selected
                        </Badge>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                    {/* Assignment Type */}
                    {assignmentOptions.length > 1 && (
                        <div className="space-y-2.5">
                            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Assign To</p>
                            <div className="grid grid-cols-3 gap-2">
                                {assignmentOptions.map((option) => {
                                    const Icon = option.icon;
                                    const isActive = assignmentType === option.value;
                                    const colorMap = {
                                        blue: { bg: "bg-blue-50 border-blue-300", icon: "bg-blue-100 text-blue-600", text: "text-blue-700" },
                                        violet: { bg: "bg-violet-50 border-violet-300", icon: "bg-violet-100 text-violet-600", text: "text-violet-700" },
                                        emerald: { bg: "bg-emerald-50 border-emerald-300", icon: "bg-emerald-100 text-emerald-600", text: "text-emerald-700" }
                                    };
                                    const colors = colorMap[option.color];

                                    return (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                setAssignmentType(option.value);
                                                setSelectedAgent("");
                                                setSelectedDesk("");
                                                setSelectedFloorManager("");
                                            }}
                                            className={`flex flex-col items-center gap-2 p-3.5 rounded-lg border-2 transition-all text-center ${
                                                isActive
                                                    ? `${colors.bg} shadow-sm`
                                                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                                            }`}
                                        >
                                            <div className={`flex items-center justify-center w-9 h-9 rounded-lg ${
                                                isActive ? colors.icon : "bg-gray-100 text-gray-400"
                                            }`}>
                                                <Icon className="h-4.5 w-4.5" />
                                            </div>
                                            <span className={`text-xs font-medium ${
                                                isActive ? colors.text : "text-gray-600"
                                            }`}>
                                                {option.label}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Agent Selection */}
                    {assignmentType === "agent" && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-gray-700">Filter by Desk</Label>
                                {desksLoading ? (
                                    <div className="flex items-center justify-center py-3">
                                        <Loader2 className="h-4 w-4 animate-spin text-gray-400 mr-2" />
                                        <span className="text-sm text-gray-400">Loading desks...</span>
                                    </div>
                                ) : (
                                    <Select
                                        value={selectedAgentDesk}
                                        onChange={handleAgentDeskChange}
                                        options={[
                                            { value: "", label: "All Desks" },
                                            ...desks.map((desk) => ({
                                                value: desk._id,
                                                label: desk.name
                                            }))
                                        ]}
                                        placeholder="Select a desk to filter users"
                                    />
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-gray-700">
                                    Select User <span className="text-red-400">*</span>
                                </Label>
                                {agentsLoading ? (
                                    <div className="flex items-center justify-center py-3">
                                        <Loader2 className="h-4 w-4 animate-spin text-gray-400 mr-2" />
                                        <span className="text-sm text-gray-400">Loading users...</span>
                                    </div>
                                ) : (
                                    <Select
                                        value={selectedAgent}
                                        onChange={setSelectedAgent}
                                        options={agents.map((agent) => ({
                                            value: agent._id,
                                            label: `${agent.firstName} ${agent.lastName} - ${
                                                agent.position?.name || agent.position?.role
                                            } ${agent.agentDesk?.name ? `(${agent.agentDesk.name})` : ""}`
                                        }))}
                                        placeholder={
                                            agents.length === 0 && selectedAgentDesk
                                                ? "No users found in selected desk"
                                                : "Choose a user to assign leads to"
                                        }
                                        searchable={true}
                                        searchPlaceholder="Search users by name, role, or desk..."
                                        disabled={agents.length === 0}
                                    />
                                )}
                                {selectedAgentDesk && agents.length === 0 && !agentsLoading && (
                                    <p className="text-xs text-gray-400">
                                        No users found in this desk. Try selecting a different desk or "All Desks".
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Floor Manager Selection */}
                    {assignmentType === "floorManager" && canAssignToFloorManager && (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700">
                                Select Floor Manager <span className="text-red-400">*</span>
                            </Label>
                            {floorManagersLoading ? (
                                <div className="flex items-center justify-center py-3">
                                    <Loader2 className="h-4 w-4 animate-spin text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-400">Loading floor managers...</span>
                                </div>
                            ) : (
                                <Select
                                    value={selectedFloorManager}
                                    onChange={setSelectedFloorManager}
                                    options={floorManagers.map((fm) => ({
                                        value: fm._id,
                                        label: `${fm.firstName} ${fm.lastName} - ${
                                            fm.position?.name || fm.position?.role
                                        }`
                                    }))}
                                    placeholder="Choose a floor manager"
                                    searchable={true}
                                    searchPlaceholder="Search floor managers..."
                                    disabled={floorManagers.length === 0}
                                />
                            )}
                            {floorManagers.length === 0 && !floorManagersLoading && (
                                <p className="text-xs text-gray-400">
                                    No floor managers found. Please create a floor manager first.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Desk Selection */}
                    {assignmentType === "desk" && canAssignToDesk && (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700">
                                Select Desk <span className="text-red-400">*</span>
                            </Label>
                            {desksLoading ? (
                                <div className="flex items-center justify-center py-3">
                                    <Loader2 className="h-4 w-4 animate-spin text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-400">Loading desks...</span>
                                </div>
                            ) : (
                                <Select
                                    value={selectedDesk}
                                    onChange={setSelectedDesk}
                                    options={desks.map((desk) => ({
                                        value: desk._id,
                                        label: desk.name
                                    }))}
                                    placeholder="Choose a desk to assign leads to"
                                />
                            )}
                        </div>
                    )}

                    {/* Progress Bar */}
                    {loading && progress.total > 0 && (
                        <div className="rounded-lg border border-indigo-200 bg-indigo-50/70 p-4">
                            <div className="flex items-center justify-between mb-2.5">
                                <div className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 text-indigo-600 animate-spin" />
                                    <span className="text-sm font-medium text-indigo-800">Assigning leads...</span>
                                </div>
                                <span className="text-sm font-semibold text-indigo-700">{progress.percentage}%</span>
                            </div>
                            <div className="w-full bg-indigo-200/60 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-indigo-600 h-2 rounded-full transition-all duration-500 ease-out"
                                    style={{ width: `${progress.percentage}%` }}
                                />
                            </div>
                            <p className="text-xs text-indigo-600 mt-1.5">
                                {progress.current.toLocaleString()} / {progress.total.toLocaleString()} leads processed
                            </p>
                        </div>
                    )}

                    {/* Info Note */}
                    <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50/70 border border-amber-200">
                        <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-700 leading-relaxed space-y-0.5">
                            <p>Previously assigned leads will be reassigned. This action cannot be undone.</p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t bg-gray-50/80 flex items-center gap-3">
                    <Button
                        onClick={handleAssign}
                        disabled={
                            loading ||
                            (assignmentType === "agent" && (!selectedAgent || agentsLoading)) ||
                            (assignmentType === "desk" && (!selectedDesk || desksLoading)) ||
                            (assignmentType === "floorManager" && (!selectedFloorManager || floorManagersLoading))
                        }
                        className="flex-1 h-10 text-sm font-medium rounded-lg shadow-sm bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Assigning...
                            </>
                        ) : (
                            <>
                                {assignmentType === "agent" ? (
                                    <UserPlus className="h-4 w-4 mr-2" />
                                ) : assignmentType === "floorManager" ? (
                                    <Users className="h-4 w-4 mr-2" />
                                ) : (
                                    <Building2 className="h-4 w-4 mr-2" />
                                )}
                                Assign {selectedLeads.length} Leads
                            </>
                        )}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={loading}
                        className="h-10 px-5 text-sm font-medium rounded-lg"
                    >
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
